import api from './api';
import type {
  Assignment,
  PaginatedAssignmentsResponse,
  AssignmentStatus,
} from '../types/assignment';
import { buildQueryString } from '@/utils/buildQueryString';

// Backend DTO types
interface BackendAssignmentDto {
  id: number;
  itemType: string;
  itemId: number;
  itemTitle: string;
  // BPE-700: the kinds the course holds ("CLE", "Skills", "Simulation"). This
  // is what names a row's type chip. `itemTypeCode` used to, but the backend
  // dropped it in Phase 10f, so the mapping silently read 'cle' for everything.
  variantKinds?: string[];
  // Phase 10a: credential-type metadata sourced from Course.LearningObjectType.
  // Null for pathway assignments and non-credentialed content (microlearning/skills).
  credentialTypeCode?: string | null;
  credentialTypeName?: string | null;
  durationMinutes: number;
  assignedAt: string;
  dueDate?: string;
  status: string;
  isOverdue: boolean;
  progress: number;
  // Number of courses on the pathway. Null/absent for course assignments.
  learningObjectCount?: number | null;
  assignedByUserId?: number;
  assignedByUserName?: string;
}

interface BackendPagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface BackendUserCourseAssignmentDto {
  id: number;
  userId: number;
  userEmail: string;
  userName: string;
  learningObjectId: number;
  learningObjectTitle: string;
  credentialTypeCode?: string | null;
  credentialTypeName?: string | null;
  learningObjectDurationMinutes: number;
  organizationId?: number;
  organizationName?: string;
  assignedAt: string;
  dueDate?: string;
  status: string;
  isOverdue: boolean;
  assignedByUserId?: number;
  assignedByUserName?: string;
}

export interface CourseAssignment {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  status: AssignmentStatus;
  dueDate: string | null;
  isOverdue: boolean;
  assignedAt: string;
}

export interface CourseAssignmentsResponse {
  assignments: CourseAssignment[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

interface BackendGroupAssignmentDto {
  id: number;
  groupId: number;
  groupName: string;
  learningObjectId?: number;
  learningObjectTitle?: string;
  variantKinds?: string[];
  credentialTypeCode?: string | null;
  credentialTypeName?: string | null;
  pathwayId?: number;
  pathwayName?: string;
  pathwayType?: string;
  assignedAt: string;
  dueDate?: string;
  assignedByUserName?: string;
  memberCount: number;
}

// Map backend status to frontend status
const mapStatus = (status: string, isOverdue: boolean): AssignmentStatus => {
  if (isOverdue) return 'overdue';
  const statusLower = status.toLowerCase();
  if (statusLower === 'notstarted' || statusLower === 'not_started') return 'not_started';
  if (statusLower === 'inprogress' || statusLower === 'in_progress') return 'in_progress';
  if (statusLower === 'completed') return 'completed';
  return 'not_started';
};



// Helper to extract date part from ISO string
const extractDatePart = (isoString: string | undefined): string => {
  if (!isoString) return '';
  const parts = isoString.split('T');
  return parts[0] ?? '';
};

// Map backend DTO to frontend Assignment type
const mapAssignment = (dto: BackendAssignmentDto): Assignment => {
  return {
    id: String(dto.id),
    learningObjectId: String(dto.itemId),
    learningObjectTitle: dto.itemTitle,
    variantKinds: dto.variantKinds ?? [],
    credentialTypeCode: dto.credentialTypeCode ?? null,
    credentialTypeName: dto.credentialTypeName ?? null,
    assignedDate: extractDatePart(dto.assignedAt),
    dueDate: extractDatePart(dto.dueDate),
    progress: dto.progress ?? 0,
    status: mapStatus(dto.status, dto.isOverdue),
    assignedBy: dto.assignedByUserName,
    itemType: dto.itemType === 'pathway' ? 'pathway' : 'course',
    duration: dto.durationMinutes,
    learningObjectCount: dto.learningObjectCount ?? undefined,
  };
};

const mapUserCourseAssignment = (dto: BackendUserCourseAssignmentDto): CourseAssignment => ({
  id: dto.id,
  userId: dto.userId,
  userName: dto.userName,
  userEmail: dto.userEmail,
  status: mapStatus(dto.status, dto.isOverdue),
  dueDate: dto.dueDate ? extractDatePart(dto.dueDate) : null,
  isOverdue: dto.isOverdue,
  assignedAt: extractDatePart(dto.assignedAt),
});

export const assignmentApi = {
  /**
   * Get all assignments for the current user.
   */
  getAll: async (): Promise<Assignment[]> => {
    const response = await api.get<BackendPagedResult<BackendAssignmentDto>>('/me/assignments?limit=100');
    return response.data.items.map(mapAssignment);
  },

  /**
   * Get overdue assignments only.
   */
  getOverdue: async (): Promise<Assignment[]> => {
    const response = await api.get<BackendAssignmentDto[]>('/me/assignments/overdue');
    return response.data.map(mapAssignment);
  },

  /**
   * Get recent assignments for dashboard (limited to 10, in_progress + completed).
   */
  getRecent: async (): Promise<PaginatedAssignmentsResponse> => {
    const qs = buildQueryString({ page: 1, limit: 10 });
    const response = await api.get<BackendPagedResult<BackendAssignmentDto>>(`/me/assignments${qs}`);
    return {
      assignments: response.data.items.map(mapAssignment),
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      hasMore: response.data.page < response.data.totalPages,
    };
  },

  /**
   * Get paginated assignment history (in_progress + completed).
   */
  getHistory: async (params: { page?: number; pageSize?: number }): Promise<PaginatedAssignmentsResponse> => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const qs = buildQueryString({ page, limit: pageSize });
    const response = await api.get<BackendPagedResult<BackendAssignmentDto>>(`/me/assignments${qs}`);
    return {
      assignments: response.data.items.map(mapAssignment),
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      hasMore: response.data.page < response.data.totalPages,
    };
  },

  /**
   * Get paginated assignments for the My Assignments page (all statuses).
   */
  getInProgress: async (params: { page?: number; pageSize?: number }): Promise<PaginatedAssignmentsResponse> => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const qs = buildQueryString({ page, limit: pageSize });
    const response = await api.get<BackendPagedResult<BackendAssignmentDto>>(`/me/assignments${qs}`);
    return {
      assignments: response.data.items.map(mapAssignment),
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      hasMore: response.data.page < response.data.totalPages,
    };
  },

  // ============================================================================
  // Admin/Organization Assignment Management
  // ============================================================================

  /**
   * Get all assignments for an organization.
   */
  getOrgAssignments: async (
    orgId: number,
    params: { page?: number; pageSize?: number; status?: string; itemType?: string }
  ): Promise<PaginatedAssignmentsResponse> => {
    const qs = buildQueryString({
      page: params.page ?? 1,
      limit: params.pageSize ?? 25,
      status: params.status,
      itemType: params.itemType,
    });
    const response = await api.get<BackendPagedResult<BackendAssignmentDto>>(
      `/organizations/${orgId}/assignments${qs}`
    );
    return {
      assignments: response.data.items.map(mapAssignment),
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      hasMore: response.data.page < response.data.totalPages,
    };
  },

  /**
   * Get learner assignments for a specific course/learning object within an organization.
   */
  getCourseAssignments: async (
    orgId: number,
    learningObjectId: number,
    params: { page?: number; pageSize?: number; status?: string; overdue?: boolean; sortBy?: string; sortDesc?: boolean }
  ): Promise<CourseAssignmentsResponse> => {
    const qs = buildQueryString({
      page: params.page ?? 1,
      limit: params.pageSize ?? 25,
      status: params.status,
      overdue: params.overdue,
      sortBy: params.sortBy,
      sortDesc: params.sortDesc,
    });
    const response = await api.get<BackendPagedResult<BackendUserCourseAssignmentDto>>(
      `/organizations/${orgId}/learning-objects/${learningObjectId}/assignments${qs}`
    );
    return {
      assignments: response.data.items.map(mapUserCourseAssignment),
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      hasMore: response.data.page < response.data.totalPages,
    };
  },

  /**
   * Get assignments for a specific user within an organization (org admin).
   */
  getOrgUserAssignments: async (
    orgId: number,
    userId: number,
    params: { page?: number; pageSize?: number; status?: string; itemType?: string }
  ): Promise<PaginatedAssignmentsResponse> => {
    const qs = buildQueryString({
      page: params.page ?? 1,
      limit: params.pageSize ?? 25,
      status: params.status,
      itemType: params.itemType,
    });
    const response = await api.get<BackendPagedResult<BackendAssignmentDto>>(
      `/organizations/${orgId}/users/${userId}/assignments${qs}`
    );
    return {
      assignments: response.data.items.map(mapAssignment),
      totalCount: response.data.totalCount,
      page: response.data.page,
      pageSize: response.data.pageSize,
      hasMore: response.data.page < response.data.totalPages,
    };
  },

  /**
   * Get course and pathway assignments for a specific group.
   */
  getGroupAssignments: async (
    orgId: number,
    groupId: number
  ): Promise<{ courses: BackendGroupAssignmentDto[]; pathways: BackendGroupAssignmentDto[] }> => {
    const qs = buildQueryString({ groupId, limit: 100 });
    const [courseRes, pathwayRes] = await Promise.all([
      api.get<BackendPagedResult<BackendGroupAssignmentDto>>(`/organizations/${orgId}/assignments/groups${qs}`),
      api.get<BackendPagedResult<BackendGroupAssignmentDto>>(`/organizations/${orgId}/assignments/groups${qs}&itemType=pathway`),
    ]);
    return {
      courses: courseRes.data.items,
      pathways: pathwayRes.data.items,
    };
  },

  /**
   * Assign a course to users and/or groups.
   */
  assignCourse: async (dto: {
    learningObjectId: number;
    userIds?: number[];
    groupIds?: number[];
    dueDate?: string;
    organizationId?: number;
  }): Promise<{ userAssignmentsCreated: number; groupAssignmentsCreated: number; skipped: number }> => {
    try {
      const response = await api.post('/assignments/learning-objects', {
        learningObjectId: dto.learningObjectId,
        userIds: dto.userIds ?? [],
        groupIds: dto.groupIds ?? [],
        dueDate: dto.dueDate,
        organizationId: dto.organizationId,
      });
      return response.data;
    } catch (err) {
      throw mapAssignmentValidationError(err);
    }
  },

  /**
   * Pre-flight validate a course assignment. Returns the same Problems[] the
   * submit endpoint would return on a 409 — the screen renders this directly
   * so the admin can see and fix issues before clicking Assign.
   */
  validateCourseAssignment: async (dto: {
    learningObjectId: number;
    userIds?: number[];
    groupIds?: number[];
  }): Promise<AssignmentValidationResult> => {
    const response = await api.post<AssignmentValidationResult>('/assignments/learning-objects/validate', {
      learningObjectId: dto.learningObjectId,
      userIds: dto.userIds ?? [],
      groupIds: dto.groupIds ?? [],
    });
    return response.data;
  },

  /**
   * Assign a pathway to users and/or groups.
   */
  assignPathway: async (dto: {
    pathwayId: number;
    userIds?: number[];
    groupIds?: number[];
    dueDate?: string;
    organizationId?: number;
  }): Promise<{ userAssignmentsCreated: number; groupAssignmentsCreated: number; skipped: number }> => {
    try {
      const response = await api.post('/assignments/pathways', {
        pathwayId: dto.pathwayId,
        userIds: dto.userIds ?? [],
        groupIds: dto.groupIds ?? [],
        dueDate: dto.dueDate,
        organizationId: dto.organizationId,
      });
      return response.data;
    } catch (err) {
      throw mapAssignmentValidationError(err);
    }
  },

  /**
   * Pre-flight validate a pathway assignment. Per-course problems land with
   * `learningObjectId` set so the UI can call out exactly which item in the
   * pathway a learner's contract doesn't cover.
   */
  validatePathwayAssignment: async (dto: {
    pathwayId: number;
    userIds?: number[];
    groupIds?: number[];
  }): Promise<AssignmentValidationResult> => {
    const response = await api.post<AssignmentValidationResult>('/assignments/pathways/validate', {
      pathwayId: dto.pathwayId,
      userIds: dto.userIds ?? [],
      groupIds: dto.groupIds ?? [],
    });
    return response.data;
  },

  /**
   * Update a user course assignment.
   */
  updateUserCourseAssignment: async (id: number, dueDate?: string): Promise<void> => {
    await api.put(`/assignments/learning-objects/users/${id}`, { dueDate });
  },

  /**
   * Update a user pathway assignment.
   */
  updateUserPathwayAssignment: async (id: number, dueDate?: string): Promise<void> => {
    await api.put(`/assignments/pathways/users/${id}`, { dueDate });
  },

  /**
   * Delete a user course assignment.
   */
  deleteUserCourseAssignment: async (id: number): Promise<void> => {
    await api.delete(`/assignments/learning-objects/users/${id}`);
  },

  /**
   * Delete a user pathway assignment.
   */
  deleteUserPathwayAssignment: async (id: number): Promise<void> => {
    await api.delete(`/assignments/pathways/users/${id}`);
  },

  /**
   * Delete a group pathway assignment.
   */
  deleteGroupPathwayAssignment: async (id: number): Promise<void> => {
    await api.delete(`/assignments/pathways/groups/${id}`);
  },

  /**
   * Delete a group course assignment.
   */
  deleteGroupCourseAssignment: async (id: number): Promise<void> => {
    await api.delete(`/assignments/learning-objects/groups/${id}`);
  },
};

// ─── Assignment validation types ────────────────────────────────────────
//
// Mirror of the backend AssignmentValidationDto shape. The /validate
// endpoints always return 200 with this body; the submit endpoints return
// the same Problems[] inside a 409 response when the gate trips. Shared
// here so the screens render the same rows regardless of which path
// surfaced them.

/**
 * Stable error codes for the validation panel. The server is the source
 * of truth for which code maps to which message; the UI just renders.
 */
export type AssignmentValidationCode =
  | 'NO_ACTIVE_CONTRACT'
  | 'AMBIGUOUS_CONTRACT'
  | 'LIBRARY_OUT_OF_SCOPE';

export interface AssignmentValidationProblem {
  code: AssignmentValidationCode | string;
  userId: number;
  userName: string;
  userEmail: string;
  /** Set on per-course pathway problems; null on single-course assignments. */
  learningObjectId: number | null;
  learningObjectTitle: string | null;
  /** Set on LIBRARY_OUT_OF_SCOPE; null for the no-contract / ambiguous cases. */
  contractId: number | null;
  contractName: string | null;
  /** Group attribution for fan-out. Null when the user was directly listed. */
  viaGroupId: number | null;
  viaGroupName: string | null;
  message: string;
}

export interface AssignmentValidationResult {
  problems: AssignmentValidationProblem[];
  hasProblems: boolean;
}

/**
 * Thrown by assignCourse / assignPathway when the backend returns 409 with
 * errorCode = ASSIGNMENT_VALIDATION_FAILED. Carries the same Problems[] the
 * /validate endpoints return, so callers can re-render the panel without
 * reaching for the raw axios error.
 */
export class AssignmentValidationError extends Error {
  readonly problems: AssignmentValidationProblem[];

  constructor(problems: AssignmentValidationProblem[], message: string) {
    super(message);
    this.name = 'AssignmentValidationError';
    this.problems = problems;
  }
}

/**
 * Narrow an axios catch-block error to AssignmentValidationError when the
 * 409 body carries the structured payload. Anything else passes through
 * unchanged so the caller's existing error handling still applies.
 */
function mapAssignmentValidationError(err: unknown): unknown {
  // Loose narrowing — axios error shape isn't typed here without pulling
  // the AxiosError import; checking the response.data shape directly is
  // safer against config/version drift.
  const data = (err as { response?: { status?: number; data?: { errorCode?: string; problems?: AssignmentValidationProblem[]; message?: string } } })?.response?.data;
  if (data?.errorCode === 'ASSIGNMENT_VALIDATION_FAILED' && Array.isArray(data.problems)) {
    return new AssignmentValidationError(data.problems, data.message ?? 'Assignment validation failed.');
  }
  return err;
}
